"use client";

import { Check, Palette } from "lucide-react";

import { cn } from "@/src/lib/utils/cn";

type InfoItem = {
  label: string;
  value: string;
};

type InfoGroup = {
  variantId?: number;
  price?: string;
  group: string;
  title?: string;
  items: InfoItem[];
  sections?: Array<{
    title: string;
    items: InfoItem[];
  }>;
};

type GroupedInfoItem =
  | {
      label: string;
      type: "text";
      value: string;
    }
  | {
      label: string;
      type: "color";
      colors: string[];
    };

function formatCurrency(value: string) {
  const parsed = Number.parseFloat(value);

  if (Number.isNaN(parsed)) {
    return `${value} جنيه`;
  }

  return `${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(parsed)} جنيه`;
}

function isHexColor(value: string) {
  return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value.trim());
}

function extractHexColors(value: string) {
  return (
    value.match(/#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3})/g) ?? []
  ).map((color) => color.trim());
}

function isColorInfoItem(item: InfoItem) {
  const label = item.label.trim();
  const rawValue = item.value.trim();

  return (
    /لون|color/i.test(label) ||
    isHexColor(rawValue) ||
    extractHexColors(rawValue).length > 0
  );
}

function groupInfoItems(items: InfoItem[]): GroupedInfoItem[] {
  const textItems: GroupedInfoItem[] = [];
  const colorMap = new Map<string, string>();
  let colorLabel = "";

  items.forEach((item) => {
    if (!isColorInfoItem(item)) {
      textItems.push({ label: item.label, type: "text", value: item.value });
      return;
    }

    const colors = extractHexColors(item.value.trim());
    const fallbackColor = isHexColor(item.value.trim())
      ? [item.value.trim()]
      : [];

    if (!colorLabel) {
      colorLabel = item.label.trim();
    }

    [...colors, ...fallbackColor].forEach((color) => {
      colorMap.set(color.toLowerCase(), color);
    });
  });

  if (colorMap.size === 0) {
    return textItems;
  }

  return [
    {
      label: colorLabel || "الألوان",
      type: "color",
      colors: Array.from(colorMap.values()),
    },
    ...textItems,
  ];
}

function InfoValue({ item }: { item: GroupedInfoItem }) {
  if (item.type === "color") {
    return (
      <span className="flex flex-wrap items-center justify-start gap-1.5">
        {item.colors.map((color) => (
          <span
            key={color}
            className="h-4 w-4 rounded-full border border-white shadow-[0_0_0_1px_rgba(238,55,106,0.22)] sm:h-5 sm:w-5"
            style={{ backgroundColor: color }}
            title={color}
          />
        ))}
      </span>
    );
  }

  return (
    <span className="block min-w-0 break-words text-xs font-bold leading-6 text-[#62565a] sm:text-sm">
      {item.value.trim()}
    </span>
  );
}

export default function ProductDetailsSpecs({
  infoGroups,
  selectedGroupIds = [],
  selectableGroupIds = [],
  onToggleGroup,
  onToggleAllGroups,
}: {
  infoGroups: InfoGroup[];
  selectedGroupIds?: number[];
  selectableGroupIds?: number[];
  onToggleGroup?: (variantId: number) => void;
  onToggleAllGroups?: () => void;
}) {
  const visibleGroups = infoGroups
    .map((group) => {
      const sections =
        group.sections && group.sections.length > 0
          ? group.sections
              .map((section) => ({
                ...section,
                groupedItems: groupInfoItems(section.items),
              }))
              .filter((section) => section.groupedItems.length > 0)
          : [
              {
                title: "",
                groupedItems: groupInfoItems(group.items),
              },
            ];

      return { ...group, sections };
    })
    .filter((group) => group.sections.length > 0);
  const hasSelectableGroups = selectableGroupIds.length > 0;
  const allGroupsSelected =
    hasSelectableGroups &&
    selectedGroupIds.length === selectableGroupIds.length;

  const desktopColumns = visibleGroups
    .map((group, originalIndex) => ({
      group,
      originalIndex,
      weight: group.sections.reduce(
        (total, section) =>
          total + section.groupedItems.length + (section.title ? 0.65 : 0),
        0,
      ),
    }))
    .sort((first, second) => second.weight - first.weight)
    .reduce<
      Array<{
        groups: Array<
          (typeof visibleGroups)[number] & { originalIndex: number }
        >;
        weight: number;
      }>
    >(
      (columns, entry) => {
        const targetColumn = columns[0].weight <= columns[1].weight ? 0 : 1;

        columns[targetColumn].groups.push({
          ...entry.group,
          originalIndex: entry.originalIndex,
        });
        columns[targetColumn].weight += entry.weight;
        return columns;
      },
      [
        { groups: [], weight: 0 },
        { groups: [], weight: 0 },
      ],
    );

  if (visibleGroups.length === 0) {
    return null;
  }

  const renderGroup = (
    group: (typeof visibleGroups)[number],
    groupIndex: number,
    layout: "mobile" | "desktop",
  ) => {
    const variantId = group.variantId;
    const isSelectable = typeof variantId === "number" && variantId > 0;
    const isSelected = isSelectable && selectedGroupIds.includes(variantId);
    const Wrapper = isSelectable ? "button" : "article";

    return (
      <Wrapper
        key={`${layout}-${group.group}-${groupIndex}`}
        type={isSelectable ? "button" : undefined}
        aria-pressed={isSelectable ? isSelected : undefined}
        onClick={isSelectable ? () => onToggleGroup?.(variantId) : undefined}
        className={cn(
          "block w-full overflow-hidden rounded-[1rem] border bg-[#fffafb] text-right transition hover:-translate-y-0.5",
          isSelected
            ? "border-[#ee376a] shadow-[0_16px_34px_rgba(238,55,106,0.12)] ring-2 ring-[rgba(238,55,106,0.16)]"
            : "border-[rgba(238,55,106,0.12)] shadow-[0_10px_22px_rgba(36,21,26,0.035)]",
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[rgba(238,55,106,0.1)] bg-white px-3 py-2.5">
          {isSelectable ? (
            <span
              className={cn(
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border",
                isSelected
                  ? "border-[#ee376a] bg-[#ee376a] text-white"
                  : "border-[rgba(238,55,106,0.2)] bg-[#fff8fb] text-transparent",
              )}
            >
              <Check className="h-4 w-4" />
            </span>
          ) : (
            <span />
          )}

          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 text-right">
            {group.price ? (
              <span className="inline-flex shrink-0 rounded-full border border-[rgba(238,55,106,0.22)] bg-[#fff0f5] px-2.5 py-1 text-xs font-black text-[#ee376a]">
                {formatCurrency(group.price)}
              </span>
            ) : null}
            <h3 className="min-w-0 break-words text-sm font-black text-[var(--color-ink)]">
              {group.title ?? group.group}
            </h3>
          </div>
        </div>

        <div>
          {group.sections.map((section, sectionIndex) => (
            <div
              key={`${group.group}-${section.title}-${sectionIndex}`}
              className="border-t border-[rgba(238,55,106,0.1)] first:border-t-0"
            >
              {section.title ? (
                <h4 className="bg-[#fff0f5] px-3 py-2 text-sm font-black text-[#ee376a]">
                  {section.title}
                </h4>
              ) : null}
              <div className="divide-y divide-[rgba(238,55,106,0.08)] px-2 py-1">
                {section.groupedItems.map((item, itemIndex) => (
                  <div
                    key={`${group.group}-${section.title}-${item.label}-${itemIndex}`}
                    className={cn(
                      "grid min-h-[2.5rem] grid-cols-[minmax(5rem,0.58fr)_minmax(0,1.42fr)] items-stretch",
                      itemIndex % 2 === 0 ? "bg-white" : "bg-[#fff9fb]",
                    )}
                  >
                    <div className="flex items-center justify-start px-2 py-1.5 text-start text-xs font-black text-[var(--color-ink)] sm:text-sm">
                      <span className="min-w-0 break-words">
                        {item.label.trim()}
                      </span>
                    </div>
                    <div className="flex min-w-0 items-center justify-start border-r border-[rgba(238,55,106,0.08)] px-2.5 py-1.5 text-start">
                      <InfoValue item={item} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Wrapper>
    );
  };

  return (
    <section
      dir="rtl"
      className="rounded-[1.35rem] border border-[rgba(238,55,106,0.12)] bg-white p-3 shadow-[0_16px_38px_rgba(36,21,26,0.045)] md:p-4"
      data-reveal="up"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[rgba(238,55,106,0.1)] pb-3 text-right">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#fff0f5] text-[#ee376a]">
            <Palette className="h-4 w-4" />
          </span>
          <h2 className="text-base font-black text-[var(--color-ink)]">
            مكونات ومواصفات الطلب
          </h2>
        </div>

        {hasSelectableGroups ? (
          <button
            type="button"
            onClick={onToggleAllGroups}
            className="inline-flex h-10 items-center justify-center rounded border border-[rgba(238,55,106,0.18)] bg-white px-3 text-xs font-black text-[#ee376a] transition hover:bg-[#fff0f5]"
          >
            {allGroupsSelected ? "إلغاء تحديد الكل" : "تحديد الكل"}
          </button>
        ) : null}
      </div>

      <div className="mt-3 space-y-3 md:hidden">
        {visibleGroups.map((group, groupIndex) =>
          renderGroup(group, groupIndex, "mobile"),
        )}
      </div>

      <div className="mt-3 hidden grid-cols-2 items-start gap-3 md:grid">
        {desktopColumns.map((column, columnIndex) => (
          <div key={`desktop-column-${columnIndex}`} className="space-y-3">
            {column.groups.map((group) =>
              renderGroup(group, group.originalIndex, "desktop"),
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
